import React, { useState, useEffect, useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  getFirestore,
  collection,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  Card,
  Table,
  Stack,
  Avatar,
  Button,
  Checkbox,
  FormControlLabel,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Tooltip,
} from "@mui/material";

import { functions } from "../firebase";
import Page from "../components/Page";
import AddMember from "./NewMember/AddMember";
import MemberDetail from "./MemberDetail";
import Scrollbar from "../components/Scrollbar";
import Iconify from "../components/Iconify";
import SearchNotFound from "../components/SearchNotFound";
import BulkImportDialog from "./BulkImportDialog";
import {
  UserListHead,
  UserListToolbar,
  UserMoreMenu,
} from "../sections/@dashboard/user";
import { fDate } from "../utils/formatTime";
import AddPlanForm from "../plans/AddPlan";
import MergeMembersDialog from "./MergeMembersDialog";

const db = getFirestore();

/* ------------------------------------------------------------------ */
/*  Table columns                                                     */
/* ------------------------------------------------------------------ */
const TABLE_HEAD = [
  { id: "avatar", label: "", alignRight: false },
  { id: "firstName", label: "First Name", alignRight: false },
  { id: "lastName", label: "Last Name", alignRight: false },
  { id: "isActive", label: "Membership Status", alignRight: false },
  { id: "lastInvoiceStatus", label: "Payment Status", alignRight: false },
  { id: "membershipType", label: "Membership Type", alignRight: false },
  { id: "membershipExpiry", label: "Membership Expiry", alignRight: false },
  { id: "actions", label: "Actions", alignRight: true },
];

/* ------------------------------------------------------------------ */
/*  Sorting & filtering                                               */
/* ------------------------------------------------------------------ */
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) return -1;
  if (b[orderBy] > a[orderBy]) return 1;
  return 0;
}
function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}
function applySortFilter(array, comparator, query, filters) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  let filtered = stabilized.map((el) => el[0]);

  if (query) {
    filtered = filtered.filter((m) => {
      const fields = [
        m.firstName?.toLowerCase() || "",
        m.lastName?.toLowerCase() || "",
        m.membershipType?.toLowerCase() || "",
        m.email?.toLowerCase() || "",
        m.phoneNumber || "",
        m.isActive ? "active" : "inactive",
      ];
      return fields.some((f) => f.includes(query.toLowerCase()));
    });
  }
  if (filters) {
    const { activeMembership, selectedMembershipType, selectedSportType } =
      filters;
    if (activeMembership)
      filtered = filtered.filter((m) => m.isActive === true);
    if (selectedMembershipType.length)
      filtered = filtered.filter((m) =>
        selectedMembershipType.includes(m.membershipType)
      );
    if (selectedSportType.length)
      filtered = filtered.filter((m) =>
        selectedSportType.some((s) => m.sportType?.includes(s))
      );
  }
  return filtered;
}

/* ------------------------------------------------------------------ */
/*  Members page                                                      */
/* ------------------------------------------------------------------ */
export default function Members() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState("firstName");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const [plan, setPlan] = useState(false);
  const [members, setMembers] = useState([]);
  const [editMember, setEditMember] = useState(null);
  const [activeMembership, setActiveMembership] = useState(false);
  const [selectedMembershipType, setSelectedMembershipType] = useState([]);
  const [selectedSportType, setSelectedSportType] = useState([]);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [memberDetailOpen, setMemberDetailOpen] = useState(false);
  const [selectedMemberDetail, setSelectedMemberDetail] = useState(null);

  /* merge dialog */
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergePair, setMergePair] = useState({ left: null, right: null });

  /* new: show only duplicates toggle */
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState(false);

  /* fetch members */
  const fetchMembers = async () => {
    try {
      const snap = await getDocs(collection(db, "members"));
      const list = snap.docs.map((d) => {
        const data = d.data();
        const activeSub = data.subscriptions?.find((s) => s.status === "ACTIVE");
  
        // helper to turn a raw value into a formatted date or null
        function safeFormat(raw) {
          if (!raw) return null;
          // if it's a Firestore Timestamp:
          const dateObj = typeof raw.toDate === "function"
            ? raw.toDate()
            : new Date(raw);
          // only format if it's a valid date
          return Number.isNaN(dateObj.getTime()) ? null : fDate(dateObj);
        }
  
        // try manual expiry first, then subscription endDate
        const manualExpiry = safeFormat(data.membershipExpiry);
        const subExpiry    = safeFormat(activeSub?.endDate);
        const membershipExpiry = manualExpiry || subExpiry || "N/A";
  
        return {
          id: d.id,
          ...data,
          membershipStatus: activeSub ? activeSub.status : "Inactive",
          lastInvoiceStatus: activeSub?.lastInvoice?.status || "N/A",
          membershipType: activeSub ? activeSub.planName : "No Plan",
          membershipExpiry,
        };
      });
      setMembers(list);
    } catch (err) {
      console.error("Error fetching members:", err);
    }
  };
  
  
  const refreshMembers = async () => {
    try {
      const sync = httpsCallable(functions, "fetchSquareCustomers");
      await sync().catch((e) =>
        console.warn("Square sync error:", e.message || e)
      );
      await fetchMembers();
    } catch (err) {
      console.error("Refresh error:", err);
    }
  };
  useEffect(() => {
    fetchMembers();
  }, [open, menu]);

  /* selection handler */
  const handleClick = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  /* open merge dialog */
  const openMergeDialog = () => {
    if (selected.length !== 2) return;
    const [l, r] = selected;
    setMergePair({
      left: members.find((m) => m.id === l),
      right: members.find((m) => m.id === r),
    });
    setMergeOpen(true);
  };
  const afterMerge = () => {
    setSelected([]);
    fetchMembers();
  };

  /* send welcome email */
  const handleSendWelcomeEmail = async () => {
    if (!selected.length) {
      alert("No members selected.");
      return;
    }
    try {
      const tmplSnap = await getDoc(
        doc(db, "emailTemplates", "welcomeEmail")
      );
      if (!tmplSnap.exists()) {
        alert("Welcome template not found.");
        return;
      }
      const { subject, html } = tmplSnap.data();
      const sendEmail = httpsCallable(
        functions,
        "sendNotificationEmail"
      );
      await Promise.all(
        selected.map(async (id) => {
          const m = members.find((u) => u.id === id);
          if (!m?.email) return;
          const body = html.replace("{{firstName}}", m.firstName);
          return sendEmail({
            to: m.email,
            subject: subject || `Welcome, ${m.firstName}!`,
            html: body,
          });
        })
      );
      alert("Welcome emails sent!");
    } catch (err) {
      console.error(err);
      alert("Failed to send welcome emails.");
    }
  };

  /* detect duplicates by email (or fallback to name) */
  const duplicateIds = useMemo(() => {
    const groups = {};
    members.forEach((m) => {
      const key = m.email?.toLowerCase() ||
        `${(m.firstName||"").toLowerCase()}|${(m.lastName||"").toLowerCase()}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(m.id);
    });
    const ids = [];
    Object.values(groups).forEach((arr) => {
      if (arr.length > 1) ids.push(...arr);
    });
    return new Set(ids);
  }, [members]);

  /* apply filters + duplicates filter */
  const filtered = applySortFilter(
    members,
    getComparator(order, orderBy),
    filterName,
    { activeMembership, selectedMembershipType, selectedSportType }
  );
  const displayed = showDuplicatesOnly
    ? filtered.filter((m) => duplicateIds.has(m.id))
    : filtered;

  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - members.length) : 0;
  const isNotFound = displayed.length === 0;

  return (
    <Page title="Members | Ballarat Squash and Racquetball">
      <Container>
        {/* header */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={5}
        >
          <Typography variant="h4">Members</Typography>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => {
                setEditMember(null);
                setOpen(true);
              }}
            >
              New Member
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:refresh-fill" />}
              onClick={refreshMembers}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:upload-outline" />}
              onClick={() => setBulkImportOpen(true)}
            >
              Import CSV
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<Iconify icon="eva:email-outline" />}
              onClick={handleSendWelcomeEmail}
            >
              Send Welcome Email
            </Button>
            <FormControlLabel
              control={
                <Checkbox
                  checked={showDuplicatesOnly}
                  onChange={(e) =>
                    setShowDuplicatesOnly(e.target.checked)
                  }
                />
              }
              label="Show Duplicates Only"
            />
            <Button
              variant="contained"
              color="warning"
              startIcon={<Iconify icon="eva:swap-outline" />}
              disabled={selected.length !== 2}
              onClick={openMergeDialog}
            >
              Merge Members
            </Button>
          </Stack>
        </Stack>

        {/* dialogs */}
        <BulkImportDialog
          isOpen={bulkImportOpen}
          toggle={() => setBulkImportOpen(false)}
        />
        <AddMember
          isOpen={open}
          toggle={() => setOpen(false)}
          edit={Boolean(editMember)}
          member={editMember}
        />
        {plan && <AddPlanForm isOpen={plan} toggle={() => setPlan(false)} />}
        <MergeMembersDialog
          open={mergeOpen}
          onClose={() => setMergeOpen(false)}
          left={mergePair.left}
          right={mergePair.right}
          afterMerge={afterMerge}
        />

        {/* table */}
        <Card>
          <UserListToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={(e) => setFilterName(e.target.value)}
            onApplyFilters={({
              activeMembership,
              selectedMembershipType,
              selectedSportType,
            }) => {
              setActiveMembership(activeMembership);
              setSelectedMembershipType(selectedMembershipType);
              setSelectedSportType(selectedSportType);
            }}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={displayed.length}
                  numSelected={selected.length}
                  onRequestSort={(e, prop) => {
                    const asc = orderBy === prop && order === "asc";
                    setOrder(asc ? "desc" : "asc");
                    setOrderBy(prop);
                  }}
                  onSelectAllClick={(e) =>
                    setSelected(
                      e.target.checked
                        ? displayed.map((m) => m.id)
                        : []
                    )
                  }
                />

                <TableBody>
                  {displayed
                    .slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    .map((row) => {
                      const isSel = selected.includes(row.id);
                      return (
                        <TableRow
                          hover
                          key={row.id}
                          selected={isSel}
                          onClick={() => {
                            setSelectedMemberDetail(row);
                            setMemberDetailOpen(true);
                          }}
                        >
                          <TableCell
                            padding="checkbox"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleClick(row.id);
                            }}
                          >
                            <Checkbox checked={isSel} />
                          </TableCell>
                          <TableCell padding="none">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                            >
                              <Avatar
                                alt={row.cin}
                                src={row.profilePic}
                              />
                            </Stack>
                          </TableCell>
                          <TableCell>{row.firstName}</TableCell>
                          <TableCell>{row.lastName}</TableCell>
                          <TableCell>
                            {row.membershipStatus}
                          </TableCell>
                          <TableCell>
                            {row.lastInvoiceStatus}
                          </TableCell>
                          <TableCell>
                            {row.membershipType}
                          </TableCell>
                          <TableCell>
                            {row.membershipExpiry}
                          </TableCell>
                          <TableCell
                            align="right"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <UserMoreMenu
                              member={row}
                              menu={menu}
                              setMenu={setMenu}
                              onEdit={(m) => {
                                setEditMember(m);
                                setOpen(true);
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}

                  {emptyRows > 0 && (
                    <TableRow
                      style={{ height: 53 * emptyRows }}
                    >
                      <TableCell colSpan={8} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        align="center"
                        sx={{ py: 3 }}
                      >
                        <SearchNotFound
                          searchQuery={filterName}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={displayed.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(
                parseInt(e.target.value, 10)
              );
              setPage(0);
            }}
          />
        </Card>

        {/* details dialog */}
        <Dialog
          open={memberDetailOpen}
          onClose={() => setMemberDetailOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            {selectedMemberDetail ? (
              <MemberDetail
                member={selectedMemberDetail}
                onClose={() => setMemberDetailOpen(false)}
              />
            ) : (
              <Typography variant="h6">
                Loading details…
              </Typography>
            )}
          </Box>
        </Dialog>
      </Container>
    </Page>
  );
}
